import React from 'react';

import { Box, Checkbox, Stack, Typography } from '@mui/material';
import { Field } from 'react-final-form';

import { IntegrationScope } from 'src/api';

import { SCOPE_OPTIONS } from '../constants';

type ScopeCardProps = {
  title: string;
  description: string;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
};

const ScopeCard: React.FC<ScopeCardProps> = ({
  title,
  description,
  checked,
  onToggle,
  disabled,
}) => {
  return (
    <Box
      component="label"
      sx={theme => ({
        display: 'flex',
        gap: 1.5,
        p: 2,
        borderRadius: 1,
        border: `1px solid ${checked ? theme.palette.primary.main : theme.palette.divider}`,
        backgroundColor: checked ? theme.palette.action.selected : 'transparent',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: theme.transitions.create(['background-color', 'border-color']),
      })}
    >
      <Checkbox
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        sx={{ p: 0, alignSelf: 'flex-start', mt: '2px' }}
      />
      <Box>
        <Typography variant="overline" lineHeight={1.4} fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

type ScopeCardsFieldProps = {
  disabled?: boolean;
};

export const ScopeCardsField: React.FC<ScopeCardsFieldProps> = ({ disabled }) => {
  return (
    <Field<IntegrationScope[]> name="scope" subscription={{ value: true }}>
      {({ input }) => {
        const value = input.value || [];
        return (
          <Stack spacing={1.5}>
            {SCOPE_OPTIONS.map(option => {
              const checked = value.includes(option.value);
              const onToggle = () => {
                const next = checked
                  ? value.filter(s => s !== option.value)
                  : [...value, option.value];
                input.onChange(next);
              };
              return (
                <ScopeCard
                  key={option.value}
                  title={option.title}
                  description={option.description}
                  checked={checked}
                  onToggle={onToggle}
                  disabled={disabled}
                />
              );
            })}
          </Stack>
        );
      }}
    </Field>
  );
};
